/**
 * RELEASE TO PRODUCTION (EXPERT-ROOMS.md §18) — the expert's equivalent of a network's release dialog, opened when a
 * release is promoted to Production (the moment others see it). Preloaded from what the build and the release already
 * hold (name, summary, tags, version, teams / rules); the person adds release notes and chooses who sees it:
 *   - this workspace only        (stops sharing when it was shared)
 *   - my company                 (listed in the company catalog IMMEDIATELY; subscribe → you approve, or auto)
 *   - also the public registry   (my company + submitted to the curated registry — reviewed before it goes live)
 * Nothing is sent until "Release" (or "Ask for approval" when a teammate must approve — the choices wait with it).
 */
import React, { useEffect, useState } from 'react';
import ConfirmDialog from '@stepflow/ui-shared/components/ConfirmDialog.jsx';

const VIS = [
  { id: 'workspace', label: 'This workspace only', hint: 'Nobody outside this workspace sees it.' },
  { id: 'company', label: 'The registry (your organization)', hint: 'Listed now — your organization\'s workspaces subscribe to use it.' },
  { id: 'registry', label: 'The registry + the public registry', hint: 'Also submitted to the public registry; curators review it before it goes live.' },
];

export default function ExpertProdReleaseDialog({ open, network, release, shared = false, asks = false, busy = false,
  onCancel, onRelease, mode = 'release', inline = false }) {
  // ``inline`` — the form EXPANDS in place (inside the surface the person is already on — never a dialog over a dialog)
  // ``mode='share'`` — the SAME dialog from Settings → Share with the company / Edit listing: the release Production
  // already runs; who sees it is the company (± the registry); notes / summary / tags are the listing's words
  const sharing = mode === 'share';
  const [notes, setNotes] = useState('');
  const [summary, setSummary] = useState('');
  const [tags, setTags] = useState('');
  const [visibility, setVisibility] = useState('company');
  const [approval, setApproval] = useState('manual');
  useEffect(() => {
    if (!open) return;
    // PRELOADED like a network's release dialog — the server's draft: the listing's words when shared, else what the
    // architect wrote (purpose · tags) + the release's notes
    const draft = network?.companySharing?.draft || {};
    setNotes((!sharing && release?.notes) || draft.notes || release?.notes || '');
    setSummary(draft.summary || network?.purpose || network?.description || '');
    setTags((draft.tags && draft.tags.length ? draft.tags : (network?.tags || [])).join(', '));
    setVisibility(sharing || shared ? 'company' : (network?.companySharing?.available === false ? 'workspace' : 'company'));
    setApproval(network?.companySharing?.approval || 'manual');
  }, [open, release?.version]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const label = release?.label || (release ? `v${release.version}` : '');
  const body = (
    <div className="space-y-3 text-left" data-testid="expert-prod-release">
      <p className="text-xs text-gray-600">
        <span className="font-medium text-gray-800">{network?.name || network?.networkId}</span> · release {label}
        {release ? ` · ${release.teams} teams · ${release.rules} rules · ${release.trained} trained answers` : ''}
      </p>
      <label className="block text-xs font-medium text-gray-700">Release notes
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={1000}
          data-testid="xrel-notes" placeholder="What changed — subscribers read this"
          className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-xs font-normal" />
      </label>
      <label className="block text-xs font-medium text-gray-700">Summary (the catalog card)
        <textarea value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={600} rows={3} data-testid="xrel-summary"
          className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-xs font-normal" />
      </label>
      <label className="block text-xs font-medium text-gray-700">Tags (comma-separated)
        <input value={tags} onChange={(e) => setTags(e.target.value)} data-testid="xrel-tags"
          className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-xs font-normal" />
      </label>
      <fieldset className="space-y-1" data-testid="xrel-visibility">
        <legend className="text-xs font-medium text-gray-700">Who sees it</legend>
        {VIS.filter((v) => (sharing ? v.id !== 'workspace' : (v.id === 'workspace' || network?.companySharing?.available !== false))).map((v) => (
          <label key={v.id} className="flex items-start gap-2 text-xs text-gray-700">
            <input type="radio" name="xrel-vis" checked={visibility === v.id} onChange={() => setVisibility(v.id)}
              data-testid={`xrel-vis-${v.id}`} />
            <span><span className="font-medium">{v.label}</span> — {v.hint}
              {v.id === 'workspace' && shared ? ' It is shared now: every subscribed workspace loses it.' : ''}</span>
          </label>
        ))}
      </fieldset>
      {visibility !== 'workspace' ? (
        <fieldset className="space-y-1" data-testid="xrel-approval">
          <legend className="text-xs font-medium text-gray-700">When a workspace subscribes</legend>
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input type="radio" name="xrel-appr" checked={approval === 'manual'} onChange={() => setApproval('manual')} />
            You approve each one
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input type="radio" name="xrel-appr" checked={approval === 'auto'} onChange={() => setApproval('auto')}
              data-testid="xrel-approval-auto" />
            Any workspace of your organization gets it right away
          </label>
        </fieldset>
      ) : null}
      {asks ? <p className="text-[11px] text-amber-700">A teammate approves before it goes live — your choices wait with the request.</p> : null}
    </div>
  );
  const title = sharing ? (shared ? 'Edit the registry listing' : 'Release to registry') : 'Release to Production';
  const confirmLabel = sharing ? (shared ? 'Save listing' : 'Release') : asks ? 'Ask for approval' : 'Release';
  const confirm = () => onRelease?.({
    notes: notes.trim(), summary: summary.trim(),
    tags: tags.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 12), visibility,
    ...(visibility !== 'workspace' ? { approval } : {}),
  });
  if (inline) {
    return (
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/30 p-4" data-testid="xrel-dialog">
        <h4 className="mb-2 text-sm font-semibold text-gray-900">{title}</h4>
        {body}
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={busy} data-testid="xrel-dialog-cancel"
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={confirm} disabled={busy} data-testid="xrel-dialog-confirm"
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    );
  }
  return (
    <ConfirmDialog open testid="xrel-dialog" title={title} message={body} confirmLabel={confirmLabel} busy={busy}
      onCancel={onCancel} onConfirm={confirm} />
  );
}
